import { useCallback, useState } from 'react';
import _ from 'lodash';
import { Button, RelatedDisputes } from 'components';
import { useDisputeRestrictions, useNavigation } from 'hooks';
import { useSetDisputeState, useImpersonateAccount } from 'state';
import { AdjustmentDetail } from 'types';
import styles from './DisputeButton.module.scss';
import { classNames } from '@progress/kendo-react-common';


type Props = {
    disabled?: boolean,
    context?: string,
    selectedDetails: AdjustmentDetail[] 
};

export default function DisputeButton({context, disabled, selectedDetails }: Props) {
    const [showRelated, setShowRelated] = useState<boolean>();
    const setDisputeRows = useSetDisputeState();
    const { navigateToCreateDispute, navigateToDispute } = useNavigation();
    const { isDisputeAllowed, relatedDisputes } = useDisputeRestrictions(selectedDetails);
    const [currentlyImpersonating] = useImpersonateAccount();
    const isDetailsContext = context === 'deductionDetails'
        || context === 'billbackDetails'
        || context === 'creditDetails'
        || context === 'repayDetails';
    const disabledMessage = context === 'deductionDetails' || context === 'billbackDetails'
        ? 'Disputes are only allowed after the adjustment is paid.'
        : 'Disputes not allowed on unpaid invoices.';
    const handleClick = useCallback(() => {
        selectedDetails && setDisputeRows([...selectedDetails]);
        navigateToCreateDispute();
    }, [navigateToCreateDispute, selectedDetails, setDisputeRows]);

    let disputeMessage = "";
    
    const relatedButton = () => {
        if (!_.isEmpty(relatedDisputes)) {
            const disputeNumber = relatedDisputes[0].disputeNumber;
            disputeMessage = (relatedDisputes.length > 1 ? "Multiple disputes have already been opened for this item." : "A dispute has already been opened for this item.") 
            return relatedDisputes.length > 1 ? (
                <div className={styles.button}>                   
                    <Button
                        className={classNames({
                            [styles.buttonViewDisputes]: context!=="gridView",
                            [styles.gridViewButtonViewDisputes]: context==="gridView",
                        })}
                        fillMode="outline"
                        themeColor="primary"
                        onClick={() => setShowRelated(true)}
                    >
                        RELATED DISPUTES
                    </Button>
                    <RelatedDisputes disputes={relatedDisputes} show={showRelated} onClose={() => setShowRelated(false)} />                   
                </div>
            ) : (
                <Button
                    className={classNames({
                        [styles.buttonViewDisputes]: context!=="gridView",
                        [styles.gridViewButtonViewDisputes]: context==="gridView",
                    })}
                    fillMode="outline"
                    themeColor="primary"
                    onClick={() => navigateToDispute(disputeNumber)}
                    title={`View dispute #${disputeNumber}`}
                >
                    VIEW DISPUTE
                </Button>
            )
        }
    };

    const displayDisputeButton = () => { 
        if (currentlyImpersonating) {
            return (
                <>
                    <div className={styles.container}>
                        {relatedButton()}
                    </div>   
                    <div className={styles.disputeMessage}>{disputeMessage}</div>                 
                    <div className={styles.userCannotDispute}>Creating disputes is not allowed while impersonating.</div>                   
                </>
            );
        } else {
            return (
                <div className={styles.container}>
                    <div className={styles.buttonContainer}>
                        {relatedButton()}
                        {isDisputeAllowed && 
                            <Button
                                className={classNames({
                                    [styles.sidebarButton]: !disabled && !context,
                                    [styles.sidebarButtonDisabled]: disabled && !context,
                                    [styles.gridViewButton]: !disabled && context==="gridView",
                                    [styles.gridViewButtonDisabled]: disabled && context==="gridView",
                                    [styles.detailsButton]: !disabled && isDetailsContext,
                                    [styles.detailsButtonDisabled]: disabled && isDetailsContext,
                                })}
                                onClick={handleClick}
                                disabled={disabled}
                            >
                                DISPUTE
                            </Button>
                        }
                    </div>
                    <div className={styles.messageContainer}>
                        {disabled ? 
                            <div className={styles.unpaidInvoiceMessage}>{disabledMessage}</div>
                        :
                            <div className={styles.disputeMessage}>{disputeMessage}</div>
                        }
                    </div>
                </div>
            );
        }
    }

    return (
        <>
            { displayDisputeButton() }
        </>
    );
}
